'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '../Icon';
import { Typography } from '../Typography';
import { cn } from '@workspace/lib';
import { theme } from '@workspace/ui/theme';

interface AccordionSection {
  id: string;
  title: React.ReactNode;
  content: React.ReactNode;
}

interface AccordionProps {
  sections: AccordionSection[];
  className?: string;
  openIds?: string[];
  onChange?: (next: string[]) => void;
  allowMultiple?: boolean;
}

export function AccordionWithCustomContent({
  sections,
  className,
  openIds,
  onChange,
  allowMultiple = false,
}: AccordionProps) {
  // ?INFO: sanitized version of sections with guaranteed-unique keys for React rendering and accordion state management.
  const normalized = useMemo(() => {
    const seen = new Map<string, number>();
    return (sections || []).map((s, i) => {
      const count = (seen.get(s.id) ?? 0) + 1;
      seen.set(s.id, count);
      const __kid = count === 1 ? s.id : `${s.id}__${i}`;
      return { ...s, __kid };
    });
  }, [sections]);

  const allIds = useMemo(() => normalized.map((s) => s.__kid), [normalized]);
  const isControlled = openIds !== undefined;
  const [internalOpenIds, setInternalOpenIds] = useState<Set<string>>(() => new Set(allIds));
  const currentOpenIds = isControlled ? new Set(openIds) : internalOpenIds;
  const backgroundColor = theme === 'redRec' ? 'bg-primary-100' : '';
  const textColor = theme === 'redRec' ? 'text-primary' : 'text-black';
  const paddingClass = theme === 'redRec' ? 'p-4' : 'pt-5 pb-2';
  useEffect(() => {
    if (!isControlled) {
      setInternalOpenIds(new Set(allIds));
    }
  }, [normalized, allIds, isControlled]);

  const toggle = (kid: string) => {
    let next: Set<string>;
    if (allowMultiple) {
      next = new Set(currentOpenIds);
      next.has(kid) ? next.delete(kid) : next.add(kid);
    } else {
      next = currentOpenIds.has(kid) ? new Set() : new Set([kid]);
    }
    if (!isControlled) setInternalOpenIds(next);
    onChange?.([...next]);
  };

  // Use Tailwind responsive variants (md: = min-width 768px) instead of a JS media query so the
  // gap height is identical on server and first client render, avoiding a hydration mismatch.
  const gapClass = (aOpen: boolean) => (aOpen ? 'h-xl md:h-2xl' : 'h-lg md:h-xl');

  return (
    <div className={cn('flex flex-col gap-0', className)}>
      {normalized.map(({ __kid, title, content }, i) => {
        const isOpen = currentOpenIds.has(__kid);

        return (
          <React.Fragment key={__kid}>
            <div className="overflow-visible">
              <button
                id={`acc-button-${__kid}`}
                type="button"
                aria-expanded={isOpen}
                aria-controls={`acc-panel-${__kid}`}
                onClick={() => toggle(__kid)}
                className={cn(
                  'w-full flex items-center justify-between text-left',
                  isOpen ? 'border-b border-primary ' + backgroundColor : 'border-b border-platinum',
                  paddingClass,
                )}
              >
                <Typography variant="h2" styleVariant="h5" className={cn('', textColor)}>
                  {title}
                </Typography>
                <span
                  className={cn(
                    'transform transition-transform duration-300 ease-in-out',
                    isOpen ? 'rotate-180' : 'rotate-0',
                  )}
                >
                  <Icon
                    name="ChevronDown"
                    size={24}
                    className={cn('transition-colors duration-200', isOpen ? 'text-primary' : 'text-gray-900')}
                  />
                </span>
              </button>

              {isOpen && (
                <div id={`acc-panel-${__kid}`} role="region" aria-labelledby={`acc-button-${__kid}`} hidden={!isOpen}>
                  <div className="md:mt-8 mt-4">{content}</div>
                </div>
              )}
            </div>

            {i < normalized.length - 1 && <div aria-hidden className={cn('shrink-0', gapClass(isOpen))} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
